import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import {
  Send, Image as ImageIcon, Sparkles, Edit3, Trash2,
  X as CloseIcon, Check, Terminal, Users, Calendar, Clock,
  Award, MapPin, BarChart2, Briefcase, ChevronRight,
  MoreHorizontal, MessageSquare, ThumbsUp, PartyPopper, Heart, Lightbulb, Smile, Share2,
  UserPlus, Zap, Trophy, Flag, Cpu, TrendingUp, Medal, ChevronDown, Reply
} from 'lucide-react';

import ReactionSelector from '../components/ReactionSelector';
import ShareModal from '../components/ShareModal';
import TagPersonnel from '../components/TagPersonnel';

const ReactionSummary = ({ summary, count, commentCount, onReactionClick, onCommentClick }) => {
  const { t } = useLanguage();
  if ((!summary || count === 0) && (!commentCount || commentCount === 0)) return null;
  const types = summary ? summary.split(',') : [];

  const getReactionStyle = (type) => {
    switch (type) {
      case 'celebrate': return { bg: '#eb4899', icon: <PartyPopper size={14} color="white" /> };
      case 'support': return { bg: '#f43f5e', icon: <Heart size={14} color="white" fill="white" style={{ transform: 'translateY(1px)' }} /> };
      case 'insightful': return { bg: '#f59e0b', icon: <Lightbulb size={14} color="white" /> };
      case 'funny': return { bg: '#0ea5e9', icon: <Smile size={14} color="white" /> };
      default: return { bg: '#3b82f6', icon: <ThumbsUp size={14} color="white" fill="white" style={{ transform: 'translateY(-1px)' }} /> };
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 1.25rem', borderTop: '1px solid var(--glass-border)' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: count > 0 ? 'pointer' : 'default' }}
        onClick={() => count > 0 && onReactionClick && onReactionClick()}
      >
        {types.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {types.map((type, i) => {
              const { bg, icon } = getReactionStyle(type);
              return (
                <div key={type} style={{
                  marginLeft: i === 0 ? 0 : '-8px',
                  zIndex: 10 - i,
                  background: bg,
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid var(--bg-card)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  flexShrink: 0
                }}>
                  {icon}
                </div>
              );
            })}
          </div>
        )}
        {count > 0 && (
          <span className="hover-underline" style={{ fontSize: '0.85rem', color: 'var(--text-heading)', fontWeight: 800, marginLeft: '4px' }}>{count}</span>
        )}
      </div>
      {commentCount > 0 && (
        <span
          className="hover-underline"
          onClick={() => onCommentClick && onCommentClick()}
          style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 500, cursor: 'pointer' }}
        >
          {commentCount} {commentCount === 1 ? t('comment_single') : t('comment_plural')}
        </span>
      )}
    </div>
  );
};

const EngagementModal = ({ isOpen, onClose, title, postId, type, pollOptions }) => {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && postId) {
      setLoading(true);
      const endpoint = type === 'reactors' ? `/posts/${postId}/reactors` :
        type === 'voters' ? `/posts/${postId}/voters` :
          `/posts/${postId}/commenters`;
      api.get(endpoint)
        .then(res => setUsers(res.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [isOpen, postId, type]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="glass-card animate-scale-up" style={{ width: '90%', maxWidth: '450px', maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: '24px' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--text-heading)' }}>{title}</h3>
          <CloseIcon size={20} color="var(--text-muted)" style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>{t('no_engagement')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {users.map(user => (
                <div
                  key={user.id}
                  className="hover-item"
                  onClick={() => { navigate(`/profile/${user.id}`); onClose(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem', borderRadius: '14px', cursor: 'pointer', background: 'rgba(255,255,255,0.02)' }}
                >
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--bg-deep)', overflow: 'hidden', flexShrink: 0 }}>
                      {user.avatar_url ? (
                        <img src={user.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)' }}>
                          {user.username[0].toUpperCase()}
                        </div>
                      )}
                    </div>
                    {type === 'reactors' && user.reaction_type && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-4px',
                        right: '-4px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: user.reaction_type === 'celebrate' ? '#eb4899' : user.reaction_type === 'support' ? '#f43f5e' : user.reaction_type === 'insightful' ? '#f59e0b' : user.reaction_type === 'funny' ? '#0ea5e9' : '#3b82f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid var(--bg-card)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        zIndex: 2
                      }}>
                        {user.reaction_type === 'celebrate' ? <PartyPopper size={10} color="white" /> :
                          user.reaction_type === 'support' ? <Heart size={10} color="white" fill="white" /> :
                            user.reaction_type === 'insightful' ? <Lightbulb size={10} color="white" /> :
                              user.reaction_type === 'funny' ? <Smile size={10} color="white" /> :
                                <ThumbsUp size={10} color="white" fill="white" />}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-heading)' }}>{user.full_name || user.username}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      {type === 'voters' && pollOptions ? (
                        <span style={{ color: 'var(--accent-primary)', fontWeight: 700 }}>
                        Voted: {pollOptions[user.option_index] || t('unknown_label')}
                        </span>
                      ) : (
                        user.role_title || t('member_label')
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfirmDialog = ({ isOpen, onConfirm, onCancel, onSave, title, message }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
      <div className="glass-card animate-scale-up" style={{ width: '90%', maxWidth: '400px', padding: '2rem', borderRadius: '24px', textAlign: 'center', border: '1px solid var(--accent-primary)' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 900, background: 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '1rem' }}>{title}</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.5', marginBottom: '2rem' }}>{message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button className="btn-cyber" onClick={onSave} style={{ width: '100%', padding: '0.8rem' }}>{t('save_changes')}</button>
          <button className="btn-cyber" onClick={onConfirm} style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: 'var(--accent-magenta)', border: '1px solid rgba(244, 63, 94, 0.3)' }}>{t('discard')}</button>
          <button className="btn-cyber" onClick={onCancel} style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)' }}>{t('cancel')}</button>
        </div>
      </div>
    </div>
  );
};

const DeleteConfirmModal = ({ isOpen, onConfirm, onCancel, title, message }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)' }}>
      <div className="glass-card animate-scale-up" style={{ width: '90%', maxWidth: '400px', padding: '2rem', borderRadius: '24px', textAlign: 'center', border: '1px solid var(--glass-border)' }}>
        <div style={{ marginBottom: '1.5rem', color: 'var(--accent-magenta)' }}>
          <Trash2 size={32} />
        </div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-heading)', marginBottom: '1rem' }}>{title}</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '2rem' }}>{message}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button className="btn-cyber" onClick={onConfirm} style={{ width: '100%', padding: '0.8rem', background: 'var(--accent-magenta)', color: 'white', border: 'none', borderRadius: '12px' }}>{t('delete_post_btn')}</button>
          <button className="btn-cyber" onClick={onCancel} style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--glass-border)', borderRadius: '12px' }}>{t('cancel')}</button>
        </div>
      </div>
    </div>
  );
};

const RichPostContent = ({ id, content, media_url, media_type, event_date, location, celebration_type, mentions, allUsers, poll_question, poll_options, poll_results, user_poll_vote, onVote, canViewVoters, onViewVoters }) => {
  const { t } = useLanguage();
  if (!content && !media_url && !event_date && !celebration_type && !poll_question) return null;

  const parsedMentions = useMemo(() => {
    try {
      return mentions ? (typeof mentions === 'string' ? JSON.parse(mentions) : mentions) : [];
    } catch (e) { return []; }
  }, [mentions]);

  const parsedPollOptions = useMemo(() => {
    try {
      return poll_options ? (typeof poll_options === 'string' ? JSON.parse(poll_options) : poll_options) : [];
    } catch (e) { return []; }
  }, [poll_options]);

  const pollStats = useMemo(() => {
    if (!poll_results) return {};
    const stats = {};
    poll_results.split(',').forEach(item => {
      const [idx, count] = item.split(':');
      stats[idx] = parseInt(count);
    });
    return stats;
  }, [poll_results]);

  const totalVotes = Object.values(pollStats).reduce((a, b) => a + b, 0);

  const renderContentWithMentions = (text) => {
    if (!text && (!parsedMentions || parsedMentions.length === 0)) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {text && <p style={{ color: 'var(--text-body)', fontSize: '1rem', margin: 0, lineHeight: '1.6', fontWeight: 500 }}>{text}</p>}

        {parsedMentions && parsedMentions.length > 0 && (
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            {parsedMentions.map(targetId => {
              const mentionedUser = allUsers?.find(u => Number(u.id) === Number(targetId));
              if (!mentionedUser) return null;
              return (
                <Link
                  key={targetId}
                  to={`/profile/${targetId}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: 'var(--accent-primary)',
                    padding: '4px 12px', fontSize: '0.75rem', fontWeight: 800,
                    borderRadius: '8px', border: '1px solid rgba(99, 102, 241, 0.2)',
                    textDecoration: 'none'
                  }}
                  className="hover-bright animate-fade"
                >
                  <div style={{ width: '18px', height: '18px', borderRadius: '5px', overflow: 'hidden', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {mentionedUser.avatar_url ? (
                      <img src={mentionedUser.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 900 }}>
                        {mentionedUser.username[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  @{mentionedUser.username}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderPoll = () => {
    if (!poll_question) return null;
    return (
      <div className="elite-glass-card" style={{
        margin: '1.25rem 0', padding: '1.5rem',
        borderRadius: '20px', border: '1px solid var(--glass-border)',
        background: 'rgba(255,255,255,0.01)', boxShadow: 'var(--shadow-inset)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.5rem' }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-cyan))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)'
          }}>
            <BarChart2 size={18} color="white" />
          </div>
          <h4 style={{ color: 'var(--text-heading)', fontSize: '1.05rem', fontWeight: 900, margin: 0, letterSpacing: '-0.01em' }}>{poll_question}</h4>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {parsedPollOptions.map((opt, i) => {
            const votes = pollStats[i] || 0;
            const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
            const isVoted = user_poll_vote === i;

            return (
              <div
                key={i}
                onClick={() => onVote && onVote(id, i)}
                style={{
                  position: 'relative', height: '48px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.03)', border: `1px solid ${isVoted ? 'var(--accent-primary)' : 'var(--glass-border)'}`,
                  overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                className="hover-bright"
              >
                {/* Progress Fill */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, height: '100%',
                  width: `${percent}%`,
                  background: isVoted ? 'linear-gradient(90deg, var(--accent-primary), var(--accent-cyan))' : 'rgba(99, 102, 241, 0.15)',
                  opacity: isVoted ? 0.2 : 0.1,
                  transition: 'width 0.8s cubic-bezier(0.19, 1, 0.22, 1)'
                }} />

                {/* Active Border Glow */}
                {isVoted && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'var(--accent-primary)', boxShadow: '0 0 10px var(--accent-primary)' }} />}

                <div style={{
                  position: 'relative', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between', padding: '0 1.25rem',
                  fontSize: '0.9rem', fontWeight: isVoted ? 800 : 500, color: isVoted ? 'var(--accent-primary)' : 'var(--text-body)'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {isVoted && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent-primary)' }} />}
                    {opt}
                  </span>
                  {totalVotes > 0 && <span style={{ opacity: 0.8, fontWeight: 900, fontFamily: 'monospace' }}>{percent}%</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '1.25rem', padding: '0.75rem 0 0', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {totalVotes} {totalVotes === 1 ? t('vote_single') : t('vote_plural')} • {user_poll_vote !== null ? t('vote_recorded') : t('vote_prompt')}
          </div>
          {canViewVoters && totalVotes > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewVoters && onViewVoters(id, parsedPollOptions); }}
              style={{
                background: 'rgba(99, 102, 241, 0.05)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                color: 'var(--accent-primary)',
                padding: '5px 12px', borderRadius: '8px',
                fontSize: '0.7rem', fontWeight: 900,
                cursor: 'pointer', transition: 'all 0.3s ease',
                textTransform: 'uppercase', letterSpacing: '0.05em'
              }}
              className="hover-bright"
            >
              {t('view_voters')}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCelebration = () => {
    if (!celebration_type) return null;
    const spotlightUser = parsedMentions?.length > 0 ? allUsers?.find(u => Number(u.id) === Number(parsedMentions[0])) : null;

    return (
      <div style={{
        margin: '1.5rem 0', padding: '2.5rem',
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(99, 102, 241, 0.15))',
        borderRadius: '24px', border: '1px solid rgba(236, 72, 153, 0.25)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
        boxShadow: '0 10px 30px rgba(236, 72, 153, 0.1)'
      }} className="celebration-card">
        <div className="confetti-overlay" />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div style={{
            background: 'rgba(236, 72, 153, 0.1)',
            width: '64px', height: '64px', borderRadius: '20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem', border: '1px solid rgba(236, 72, 153, 0.2)'
          }}>
            <Award size={32} color="var(--accent-magenta)" />
          </div>

          <h4 style={{ color: 'var(--accent-magenta)', fontSize: '0.8rem', letterSpacing: '0.2em', fontWeight: 900, marginBottom: '1rem', textTransform: 'uppercase' }}>
            {t('personnel_celebration')}
          </h4>

          <h3 style={{
            color: 'var(--text-heading)', fontSize: '1.75rem', marginBottom: '1.5rem', fontWeight: 900,
            background: 'linear-gradient(to right, #fff, var(--accent-magenta))',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.02em'
          }}>
            {celebration_type.toUpperCase().replace('_', ' ')}
          </h3>

          {spotlightUser && (
            <div style={{
              margin: '1.5rem auto', padding: '1.25rem 2rem', background: 'rgba(255,255,255,0.03)',
              borderRadius: '20px', border: '1px solid var(--glass-border)', display: 'inline-flex',
              alignItems: 'center', gap: '1.25rem', animation: 'pulseGlow 3s infinite'
            }}>
              <div style={{ width: '60px', height: '60px', borderRadius: '18px', overflow: 'hidden', border: '2px solid var(--accent-magenta)', boxShadow: '0 0 20px rgba(236, 72, 153, 0.3)' }}>
                {spotlightUser.avatar_url ? (
                  <img src={spotlightUser.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 900, color: 'var(--accent-magenta)', background: 'var(--bg-deep)' }}>
                    {spotlightUser.username[0].toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 900, color: 'var(--text-heading)' }}>{spotlightUser.full_name || spotlightUser.username}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--accent-magenta)', fontWeight: 800, textTransform: 'uppercase' }}>AP Group Elite Member</div>
              </div>
            </div>
          )}

          {content && <p style={{ color: 'var(--text-body)', fontSize: '1.1rem', lineHeight: '1.6', fontWeight: 500, maxWidth: '500px', margin: '1rem auto 0' }}>{content}</p>}
        </div>
      </div>
    );
  };

  const renderEvent = () => {
    if (!event_date) return null;
    const dateObj = new Date(event_date);

    return (
      <div style={{
        margin: '1.5rem 0',
        background: 'rgba(255,255,255,0.01)',
        borderRadius: '24px',
        border: '1px solid var(--glass-border)',
        overflow: 'hidden',
        boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          padding: '1.5rem 2rem',
          background: 'linear-gradient(to right, rgba(99, 102, 241, 0.1), transparent)',
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '42px', height: '42px', borderRadius: '12px', background: 'var(--accent-primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white'
            }}>
              <Calendar size={20} />
            </div>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--accent-primary)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{t('tool_event')}</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--text-heading)' }}>Strategic Network Mission</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-heading)', lineHeight: 1 }}>{dateObj.getDate()}</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase' }}>{dateObj.toLocaleDateString('en-US', { month: 'short' })}</div>
          </div>
        </div>

        <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--glass-border)'
            }}>
              <Clock size={20} color="var(--accent-cyan)" />
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', marginBottom: '2px' }}>Temporal Alignment</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--accent-cyan)' }}>
                {dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--glass-border)'
            }}>
              <MapPin size={20} color="var(--accent-primary)" />
            </div>
            <div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase', marginBottom: '2px' }}>Strategic Venue</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-heading)' }}>{location || t('remote_mission')}</div>
            </div>
          </div>
        </div>

        {content && !celebration_type && (
          <div style={{ padding: '0 2rem 2rem' }}>
            <p style={{ color: 'var(--text-body)', fontSize: '0.95rem', margin: 0, opacity: 0.8, lineHeight: '1.6' }}>{content}</p>
          </div>
        )}
      </div>
    );
  };

  const renderMedia = () => {
    if (!media_url) return null;
    return (
      <div style={{ margin: '1.5rem 0', borderRadius: '24px', overflow: 'hidden', border: '1px solid var(--glass-border)', boxShadow: '0 15px 45px rgba(0,0,0,0.3)' }}>
        {media_type === 'video' ? (
          <video src={media_url} controls style={{ width: '100%', maxHeight: '600px', display: 'block' }} />
        ) : (
          <img src={media_url} alt="Post content" style={{ width: '100%', maxHeight: '800px', objectFit: 'cover', display: 'block' }} />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {renderCelebration()}
      {!celebration_type && renderContentWithMentions(content)}
      {renderPoll()}
      {renderEvent()}
      {renderMedia()}
    </div>
  );
};


function Home() {
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ members: 0, posts: 0, opportunities: 0 });
  const [celebrations, setCelebrations] = useState([]);
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [isCelebrationDropdownOpen, setIsCelebrationDropdownOpen] = useState(false);
  const [activeMoreMenu, setActiveMoreMenu] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [originalPost, setOriginalPost] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [postIdToDelete, setPostIdToDelete] = useState(null);

  const CELEBRATION_TYPES = [
    { id: 'New Member Joining', label: t('celebration_type_announcement'), icon: <UserPlus size={16} /> },
    { id: 'Operational Excellence', label: t('tool_celebration'), icon: <Zap size={16} /> },
    { id: 'Work Anniversary', label: t('tool_celebration'), icon: <Trophy size={16} /> },
    { id: 'Promotion', label: t('tool_celebration'), icon: <TrendingUp size={16} /> },
    { id: 'Project Accomplishment', label: t('tool_celebration'), icon: <Flag size={16} /> },
    { id: 'Skill Empowerment', label: t('tool_celebration'), icon: <Cpu size={16} /> },
    { id: 'Collective Milestone', label: t('tool_celebration'), icon: <Users size={16} /> },
    { id: 'Recognition Honor', label: t('tool_celebration'), icon: <Medal size={16} /> },
    { id: 'Cultural Contribution', label: t('tool_celebration'), icon: <Sparkles size={16} /> },
  ];
  const [postMetadata, setPostMetadata] = useState({
    type: 'post', media_url: '', event_date: '', location: '',
    celebration_type: '', mentions: [], poll_question: '', poll_options: []
  });

  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const fileInputRef = useRef(null);

  const handleMediaUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploadingMedia(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPostMetadata(prev => ({ ...prev, media_url: data.url }));
    } catch (err) {
      alert('Strategic asset upload failed');
    }
    setIsUploadingMedia(false);
  };
  const [allUsers, setAllUsers] = useState([]);
  const [activeComments, setActiveComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [hoveredPost, setHoveredPost] = useState(null);
  const [engagementModal, setEngagementModal] = useState({ isOpen: false, type: 'reactors', postId: null, title: '' });
  const [shareModal, setShareModal] = useState({ isOpen: false, post: null });
  const [editingComment, setEditingComment] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [replyingTo, setReplyingTo] = useState({ postId: null, commentId: null, username: null });
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchPosts();
    fetchProfile();
    fetchStats();
    fetchCelebrations();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (isCreatorOpen || showConfirm || showDeleteConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [isCreatorOpen, showConfirm, showDeleteConfirm]);

  useEffect(() => {
    const handleGlobalClick = () => setActiveMoreMenu(null);
    if (activeMoreMenu) {
      window.addEventListener('click', handleGlobalClick);
    }
    return () => window.removeEventListener('click', handleGlobalClick);
  }, [activeMoreMenu]);

  const isDirty = () => {
    if (!originalPost && !newPost.trim() && !postMetadata.media_url && !postMetadata.poll_question && !postMetadata.celebration_type) return false;
    if (!originalPost) return true;

    const currentMeta = JSON.stringify(postMetadata);
    const originalMeta = JSON.stringify(originalPost.metadata);
    return newPost !== originalPost.content || currentMeta !== originalMeta;
  };

  const handleCloseAttempt = () => {
    if (isDirty()) {
      setShowConfirm(true);
    } else {
      closeCreator();
    }
  };

  const closeCreator = () => {
    setIsCreatorOpen(false);
    setEditingPostId(null);
    setNewPost('');
    setPostMetadata({ type: 'post', media_url: '', event_date: '', location: '', celebration_type: '', mentions: [], poll_question: '', poll_options: [] });
    setOriginalPost(null);
    setShowConfirm(false);
  };

  const fetchPosts = async () => {
    try {
      const { data } = await api.get('/posts');
      setPosts(data);
      recordImpressions(data);
    } catch (err) { console.error('Failed to fetch posts'); }
  };

  const recordImpressions = async (postsList) => {
    try {
      const unseenPostIds = postsList
        .filter(post => {
          const sessionKey = `viewed_post_${post.id}`;
          const isOwnPost = Number(post.user_id) === Number(currentUser?.id);
          return !isOwnPost && !sessionStorage.getItem(sessionKey);
        })
        .map(p => p.id);

      if (unseenPostIds.length > 0) {
        await api.post('/posts/record-impressions', { postIds: unseenPostIds });
        unseenPostIds.forEach(id => sessionStorage.setItem(`viewed_post_${id}`, 'true'));
      }
    } catch (err) { console.error('Failed to record impressions'); }
  };

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/users/me');
      setProfile(data);
    } catch (err) { console.error('Failed to fetch profile'); }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get('/users/stats/overview');
      setStats(data);
    } catch (err) { console.error('Failed to fetch stats'); }
  };

  const fetchCelebrations = async () => {
    try {
      const { data } = await api.get('/users/celebrations/today');
      setCelebrations(data);
    } catch (err) { console.error('Failed to fetch celebrations'); }
  };

  const fetchAllUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setAllUsers(data);
    } catch (err) { console.error('Failed to fetch users'); }
  };

  const handleVote = async (postId, optionIndex) => {
    try {
      await api.post(`/posts/${postId}/vote`, { optionIndex });
      fetchPosts();
    } catch (err) {
      console.error('Failed to vote');
    }
  };

  const handlePost = async () => {
    if (!newPost.trim() && !postMetadata.media_url && !postMetadata.poll_question && !postMetadata.event_date && !postMetadata.celebration_type) return;
    try {
      if (editingPostId) {
        await api.put(`/posts/${editingPostId}`, {
          content: newPost,
          ...postMetadata
        });
      } else {
        await api.post('/posts', {
          content: newPost,
          ...postMetadata
        });
      }
      setNewPost('');
      setPostMetadata({ type: 'post', media_url: '', event_date: '', location: '', celebration_type: '', mentions: [], poll_question: '', poll_options: [] });
      setIsCreatorOpen(false);
      setEditingPostId(null);
      fetchPosts();
      fetchStats();
    } catch (err) {
      console.error('Post failed');
    }
  };

  const handleDeletePost = (postId) => {
    setPostIdToDelete(postId);
    setShowDeleteConfirm(true);
    setActiveMoreMenu(null);
  };

  const confirmDelete = async () => {
    if (!postIdToDelete) return;
    try {
      await api.delete(`/posts/${postIdToDelete}`);
      setPostIdToDelete(null);
      setShowDeleteConfirm(false);
      fetchPosts();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete post');
    }
  };

  const handleEditPost = (post) => {
    const content = post.content || '';
    const metadata = {
      type: post.type || 'post',
      media_url: post.media_url || '',
      event_date: post.event_date ? new Date(post.event_date).toISOString().slice(0, 16) : '',
      location: post.location || '',
      celebration_type: post.celebration_type || '',
      mentions: post.mentions || [],
      poll_question: post.poll_question || '',
      poll_options: post.poll_options || []
    };
    setNewPost(content);
    setPostMetadata(metadata);
    setOriginalPost({ content, metadata });
    setEditingPostId(post.id);
    setIsCreatorOpen(true);
    setActiveMoreMenu(null);
  };

  const handleLike = async (postId, type = 'like') => {
    try {
      await api.post(`/social/posts/${postId}/like`, { type });
      fetchPosts();
    } catch (err) { console.error('Reaction failed'); }
  };

  const toggleComments = async (postId) => {
    if (activeComments[postId]) {
      setActiveComments(prev => ({ ...prev, [postId]: false }));
    } else {
      setActiveComments(prev => ({ ...prev, [postId]: true }));
      try {
        const { data } = await api.get(`/social/posts/${postId}/comments`);
        setPostComments(prev => ({ ...prev, [postId]: data }));
      } catch (err) { console.error('Failed to fetch comments'); }
    }
  };

  const handleComment = async (postId, content, parentId = null) => {
    if (!content.trim()) return;
    try {
      await api.post(`/social/posts/${postId}/comments`, { content, parentId });
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      setReplyingTo({ postId: null, commentId: null, username: null });
      fetchPosts();
    } catch (err) { console.error('Comment submission failed'); }
  };

  const handleUpdateComment = async (postId, commentId, content) => {
    if (!content.trim()) return;
    try {
      await api.put(`/social/comments/${commentId}`, { content });
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      setEditingComment(null);
    } catch (err) { console.error('Comment update failed'); }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (deletingCommentId !== commentId) {
      setDeletingCommentId(commentId);
      return;
    }
    try {
      await api.delete(`/social/comments/${commentId}`);
      const { data } = await api.get(`/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: data }));
      setDeletingCommentId(null);
      fetchPosts();
    } catch (err) {
      console.error('Comment deletion failed:', err);
    }
  };

  const getReactionIcon = (type) => {
    switch (type) {
      case 'celebrate': return <PartyPopper size={16} color="var(--accent-magenta)" />;
      case 'support': return <Heart size={16} color="#f43f5e" fill="#f43f5e" />;
      case 'insightful': return <Lightbulb size={16} color="#f59e0b" />;
      case 'funny': return <Smile size={16} color="#0ea5e9" />;
      case 'like': return <ThumbsUp size={16} color="var(--accent-primary)" />;
      default: return <ThumbsUp size={16} />;
    }
  };

  const getReactionLabel = (type) => {
    switch (type) {
      case 'celebrate': return t('reaction_celebrate');
      case 'support': return t('reaction_support');
      case 'insightful': return t('reaction_insightful');
      case 'funny': return t('reaction_funny');
      case 'like': return t('reaction_like');
      default: return t('reaction_like');
    }
  };

  const getReactionColor = (type) => {
    switch (type) {
      case 'celebrate': return 'var(--accent-magenta)';
      case 'support': return '#f43f5e';
      case 'insightful': return '#f59e0b';
      case 'funny': return '#0ea5e9';
      case 'like': return 'var(--accent-primary)';
      default: return 'var(--text-muted)';
    }
  };

  const toggleMention = (userId) => {
    setPostMetadata(prev => {
      const mentions = prev.mentions.includes(userId)
        ? prev.mentions.filter(id => id !== userId)
        : [...prev.mentions, userId];
      return { ...prev, mentions };
    });
  };

  return (
    <>
      <div className="grid-to-stack" style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr minmax(300px, 1fr)', gap: '2rem' }}>
        {/* LEFT SIDEBAR: User Summary */}
        <div className="animate-fade hide-mobile">
          <div
            className="glass-card clickable-profile-card"
            style={{ padding: '0', overflow: 'hidden', position: 'sticky', top: '2rem', cursor: 'pointer' }}
            onClick={() => navigate('/profile/me')}
          >
            <div style={{
              height: '110px',
              backgroundImage: profile?.banner_url ? `url(${profile.banner_url})` : 'linear-gradient(135deg, var(--bg-apex), var(--accent-primary))',
              backgroundSize: 'cover',
              backgroundPosition: '50% 50%',
              backgroundRepeat: 'no-repeat',
              position: 'relative'
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.4))' }}></div>
            </div>
            <div style={{ padding: '1.5rem', textAlign: 'center', marginTop: '-50px' }}>
              <div style={{
                width: '84px', height: '84px', borderRadius: '24px', margin: '0 auto 1rem',
                border: '4px solid var(--bg-card)', background: 'var(--bg-card)', overflow: 'hidden',
                boxShadow: 'var(--shadow-apex)', position: 'relative', zIndex: 1
              }}>
                {profile?.avatar_url ? <img src={profile.avatar_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={32} color="var(--accent-primary)" /></div>}
              </div>
              <h3 style={{ fontSize: '1.25rem', color: 'var(--text-heading)', marginBottom: '0.25rem' }}>{profile?.full_name || (typeof currentUser?.username === 'object' ? 'User' : (currentUser?.username || 'User'))}</h3>
              <p style={{ fontSize: '0.8rem', color: 'var(--accent-primary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1.25rem' }}>{profile?.role_title || t('member_label')}</p>

              <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: '1.25rem', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('profile_views')}</span>
                  <span style={{ color: 'var(--accent-primary)', fontWeight: 800 }}>{typeof profile?.profile_views === 'object' ? 0 : (profile?.profile_views || 0)}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('total_engagement')}</span>
                    <span style={{ color: 'var(--accent-cyan)', fontWeight: 800 }}>{typeof profile?.total_engagement === 'object' ? 0 : (profile?.total_engagement || 0)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', fontSize: '0.7rem', color: 'var(--text-muted)', opacity: 0.8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }} title={t('eng_likes')}>
                      <ThumbsUp size={10} color="var(--accent-primary)" />
                      {typeof profile?.engagement_breakdown?.likes === 'object' ? 0 : (profile?.engagement_breakdown?.likes || 0)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }} title={t('eng_special')}>
                      <Sparkles size={10} color="var(--accent-magenta)" />
                      {typeof profile?.engagement_breakdown?.special === 'object' ? 0 : (profile?.engagement_breakdown?.special || 0)}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }} title={t('eng_comments')}>
                      <MessageSquare size={10} color="var(--accent-cyan)" />
                      {typeof profile?.engagement_breakdown?.comments === 'object' ? 0 : (profile?.engagement_breakdown?.comments || 0)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{t('network_size')}</span>
                  <span style={{ color: 'var(--accent-cyan)', fontWeight: 800 }}>{typeof stats.members === 'object' ? 0 : stats.members}</span>
                </div>
              </div>
            </div>
            <div
              onClick={(e) => { e.stopPropagation(); navigate('/profile/me'); }}
              style={{ padding: '1rem', background: 'rgba(255,255,255,0.02)', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textAlign: 'center', cursor: 'pointer', borderTop: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'all 0.3s ease' }}
              className="hover-bright"
            >
              <Edit3 size={14} /> {t('manage_identity')}
            </div>
          </div>
        </div>

        {/* CENTER: Post Creator & Feed */}
        <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <div className="glass-card home-feed-mobile-bleed" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'var(--bg-deep)', overflow: 'hidden', flexShrink: 0 }}>
                {profile?.avatar_url ? <img src={profile.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Users size={20} color="var(--accent-primary)" style={{ margin: '14px' }} />}
              </div>
              <button
                onClick={() => setIsCreatorOpen(true)}
                style={{ flex: 1, textAlign: 'left', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--glass-border)', borderRadius: '24px', padding: '0 1.5rem', color: 'var(--text-muted)', fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer' }}
              >
                {t('start_update')}
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
              <button className="post-tool-btn" onClick={() => { setIsCreatorOpen(true); setPostMetadata(p => ({ ...p, type: 'post' })); }}>
                <ImageIcon size={18} color="var(--accent-cyan)" /> <span style={{ fontSize: '0.95rem' }}>{t('tool_media')}</span>
              </button>
              <button className="post-tool-btn" onClick={() => { setIsCreatorOpen(true); setPostMetadata(p => ({ ...p, type: 'event' })); }}>
                <Calendar size={18} color="#818cf8" /> <span style={{ fontSize: '0.95rem' }}>{t('tool_event')}</span>
              </button>
              <button className="post-tool-btn" onClick={() => { setIsCreatorOpen(true); setPostMetadata(p => ({ ...p, type: 'celebration' })); }}>
                <Award size={18} color="var(--accent-magenta)" /> <span style={{ fontSize: '0.95rem' }}>{t('tool_celebration')}</span>
              </button>
              <button className="post-tool-btn" onClick={() => { setIsCreatorOpen(true); setPostMetadata(p => ({ ...p, type: 'poll' })); }}>
                <BarChart2 size={18} color="var(--accent-primary)" /> <span style={{ fontSize: '0.95rem' }}>{t('tool_poll')}</span>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {posts.length === 0 ? (
              <div className="glass-card home-feed-mobile-bleed" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <Users size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
                <p style={{ fontSize: '1rem', fontWeight: 600 }}>{t('feed_quiet')}</p>
                <p style={{ fontSize: '0.85rem', opacity: 0.7 }}>{t('no_updates_sector')}</p>
              </div>
            ) : (
              posts.map(post => (
                <div className="glass-card home-feed-mobile-bleed" key={post.id} style={{ padding: '0', overflow: 'visible' }}>
                  <div style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
                      <Link to={`/profile/${post.user_id}`} style={{ textDecoration: 'none', display: 'flex', gap: '1rem', alignItems: 'center', flex: 1 }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', border: '1px solid var(--glass-border)', overflow: 'hidden', flexShrink: 0 }}>
                          {post.avatar_url ? <img src={post.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'var(--accent-primary)' }}>{post.username[0].toUpperCase()}</div>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '1rem' }}>{post.full_name || post.username}</h4>
                          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>{post.role_title || t('member_label')} • {new Date(post.created_at).toLocaleDateString()}</p>
                        </div>
                      </Link>

                      {Number(currentUser?.id) === Number(post.user_id) && (
                        <div style={{ position: 'relative' }}>
                          <MoreHorizontal
                            size={18}
                            color="var(--text-muted)"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMoreMenu(activeMoreMenu === post.id ? null : post.id);
                            }}
                          />
                          {activeMoreMenu === post.id && (
                            <div
                              className="glass-card animate-scale-up"
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                zIndex: 100,
                                width: '140px',
                                background: 'var(--bg-card)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                                border: '1px solid var(--glass-border)',
                                padding: '0.5rem'
                              }}
                            >
                              <div
                                className="hover-bright"
                                style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--text-body)', fontSize: '0.85rem' }}
                                onClick={() => handleEditPost(post)}
                              >
                                <Edit3 size={14} color="var(--accent-cyan)" /> {t('edit_post')}
                              </div>
                              <div
                                className="hover-bright"
                                style={{ padding: '0.6rem 0.75rem', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#f43f5e', fontSize: '0.85rem' }}
                                onClick={() => {
                                  handleDeletePost(post.id);
                                  setActiveMoreMenu(null);
                                }}
                              >
                                <Trash2 size={14} color="#f43f5e" /> {t('delete_post')}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <RichPostContent
                      {...post}
                      allUsers={allUsers}
                      onVote={handleVote}
                      canViewVoters={currentUser?.role === 'ap_admin' || Number(currentUser?.id) === Number(post.user_id)}
                      onViewVoters={(postId, options) => setEngagementModal({ isOpen: true, type: 'voters', postId, title: t('poll_engagement_title'), pollOptions: options })}
                    />

                  </div>

                  <ReactionSummary
                    summary={post.reaction_summary}
                    count={post.like_count}
                    commentCount={post.comment_count}
                    onReactionClick={() => setEngagementModal({ isOpen: true, type: 'reactors', postId: post.id, title: t('reactions_modal_title') })}
                    onCommentClick={() => toggleComments(post.id)}
                  />

                  <div style={{ display: 'flex', borderTop: '1px solid var(--glass-border)', padding: '0.5rem' }}>
                    <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
                      <button
                        className="post-action-btn hover-bright"
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          color: getReactionColor(post.user_reaction),
                          fontWeight: post.user_reaction ? 900 : 600,
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleLike(post.id, post.user_reaction || 'like')}
                        onMouseEnter={() => setHoveredPost(post.id)}
                        onMouseLeave={() => setHoveredPost(null)}
                      >
                        {getReactionIcon(post.user_reaction)} {getReactionLabel(post.user_reaction)}
                        <ReactionSelector
                          isOpen={hoveredPost === post.id}
                          onSelect={(type) => handleLike(post.id, type)}
                          onClose={() => setHoveredPost(null)}
                        />
                      </button>
                    </div>
                    <button
                      className="post-action-btn"
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                      onClick={() => toggleComments(post.id)}
                    >
                      <MessageSquare size={18} /> {t('comment_action')}
                    </button>
                    <button
                      className="post-action-btn"
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                      onClick={() => setShareModal({ isOpen: true, post })}
                    >
                      <Share2 size={18} /> {t('share_action')}
                    </button>
                  </div>

                  {
                    activeComments[post.id] && (
                      <div style={{ padding: '0.75rem 1.5rem', borderTop: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.01)' }}>
                        {/* Comment Input */}
                        <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
                          <div style={{
                            background: 'rgba(255,255,255,0.02)',
                            borderRadius: '16px',
                            border: '1px solid var(--glass-border)',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            boxShadow: replyingTo.postId === post.id ? '0 0 20px rgba(99, 102, 241, 0.1)' : 'none',
                            borderColor: replyingTo.postId === post.id ? 'rgba(99, 102, 241, 0.3)' : 'var(--glass-border)'
                          }}>
                            {replyingTo.postId === post.id && (
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.5rem 1rem', background: 'rgba(99, 102, 241, 0.05)',
                                borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                                fontSize: '0.75rem', color: 'var(--accent-primary)',
                                fontWeight: 800, letterSpacing: '0.02em'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <Reply size={12} />
                                  <span>{t('replying_to')} <span style={{ color: 'var(--text-heading)' }}>@{replyingTo.username}</span></span>
                                </div>
                                <CloseIcon
                                  size={14}
                                  style={{ cursor: 'pointer', opacity: 0.6 }}
                                  className="hover-bright"
                                  onClick={() => setReplyingTo({ postId: null, commentId: null, username: null })}
                                />
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.75rem 1rem' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--glass-border)' }}>
                                {profile?.avatar_url ? <img src={profile.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)', fontSize: '0.7rem' }}>{currentUser?.username[0].toUpperCase()}</div>}
                              </div>
                              <input
                                type="text"
                                className="cyber-input"
                                placeholder={replyingTo.postId === post.id ? t('write_reply') : t('add_comment')}
                                style={{
                                  flex: 1, padding: '0.4rem 0', fontSize: '0.85rem',
                                  background: 'transparent', border: 'none', boxShadow: 'none'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleComment(post.id, e.target.value, replyingTo.postId === post.id ? replyingTo.commentId : null);
                                    e.target.value = '';
                                  }
                                }}
                                autoFocus={replyingTo.postId === post.id}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Threaded Comment Rendering */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {postComments[post.id]?.filter(c => !c.parent_id).map(comment => (
                            <div key={comment.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <Link to={`/profile/${comment.user_id}`} style={{ width: '32px', height: '32px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, marginTop: '2px' }}>
                                  {comment.avatar_url ? <img src={comment.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)', fontSize: '0.8rem' }}>{comment.username[0].toUpperCase()}</div>}
                                </Link>
                                <div style={{ flex: 1, background: 'rgba(255,255,255,0.02)', padding: '0.75rem 1rem', borderRadius: '14px', border: '1px solid var(--glass-border)' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.85rem' }}>
                                      {comment.full_name || comment.username}
                                      <span style={{ marginLeft: '0.5rem', fontWeight: 500, color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                                        {new Date(comment.created_at).toLocaleDateString()}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                      <button
                                        onClick={() => setReplyingTo({ postId: post.id, commentId: comment.id, username: comment.username })}
                                        style={{
                                          background: 'rgba(255,255,255,0.03)',
                                          border: '1px solid var(--glass-border)',
                                          color: 'var(--text-muted)',
                                          fontSize: '0.65rem',
                                          fontWeight: 800,
                                          cursor: 'pointer',
                                          padding: '3px 8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          borderRadius: '6px',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          transition: 'all 0.2s ease'
                                        }}
                                        className="hover-bright"
                                      >
                                        <Reply size={10} style={{ opacity: 0.8 }} /> {t('reply')}
                                      </button>
                                      {Number(comment.user_id) === Number(currentUser?.id) && (
                                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                                          <Edit3 size={12} style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => setEditingComment({ id: comment.id, content: comment.content })} />
                                          <Trash2 size={12} style={{ cursor: 'pointer', opacity: 0.6, color: deletingCommentId === comment.id ? '#f43f5e' : 'inherit' }} onClick={() => handleDeleteComment(post.id, comment.id)} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {editingComment?.id === comment.id ? (
                                    <div style={{ marginTop: '0.5rem' }}>
                                      <input
                                        autoFocus
                                        className="cyber-input"
                                        value={editingComment.content}
                                        onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') handleUpdateComment(post.id, comment.id, editingComment.content);
                                          if (e.key === 'Escape') setEditingComment(null);
                                        }}
                                        style={{ width: '100%', fontSize: '0.85rem', padding: '4px 8px' }}
                                      />
                                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.4rem' }}>
                                        <button onClick={() => handleUpdateComment(post.id, comment.id, editingComment.content)} style={{ background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer' }}>{t('save_upper')}</button>
                                        <button onClick={() => setEditingComment(null)} style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer' }}>{t('cancel_upper')}</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <p style={{ fontSize: '0.85rem', color: 'var(--text-body)', marginTop: '0.25rem', lineHeight: '1.5' }}>{comment.content}</p>
                                  )}
                                </div>
                              </div>

                              {/* Render Replies */}
                              {postComments[post.id]?.filter(reply => reply.parent_id === comment.id).map(reply => (
                                <div key={reply.id} style={{ display: 'flex', gap: '0.75rem', paddingLeft: '2.5rem' }}>
                                  <Link to={`/profile/${reply.user_id}`} style={{ width: '24px', height: '24px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0, marginTop: '2px' }}>
                                    {reply.avatar_url ? <img src={reply.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)', fontSize: '0.6rem' }}>{reply.username[0].toUpperCase()}</div>}
                                  </Link>
                                  <div style={{ flex: 1, background: 'rgba(255,255,255,0.01)', padding: '0.5rem 0.75rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                      <div style={{ fontWeight: 800, color: 'var(--text-heading)', fontSize: '0.8rem' }}>
                                        {reply.full_name || reply.username}
                                        <span style={{ marginLeft: '0.5rem', fontWeight: 500, color: 'var(--text-muted)', fontSize: '0.65rem' }}>
                                          {new Date(reply.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                        <button
                                          onClick={() => setReplyingTo({ postId: post.id, commentId: comment.id, username: reply.username })}
                                          style={{
                                            background: 'rgba(255,255,255,0.03)',
                                            border: '1px solid var(--glass-border)',
                                            color: 'var(--text-muted)',
                                            fontSize: '0.6rem',
                                            fontWeight: 800,
                                            cursor: 'pointer',
                                            padding: '2px 6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            borderRadius: '5px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            transition: 'all 0.2s ease',
                                            marginRight: '0.3rem'
                                          }}
                                          className="hover-bright"
                                        >
                                          <Reply size={9} style={{ opacity: 0.8 }} /> {t('reply')}
                                        </button>
                                        {Number(reply.user_id) === Number(currentUser?.id) && (
                                          <>
                                            <Edit3 size={10} style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => setEditingComment({ id: reply.id, content: reply.content })} />
                                            <Trash2 size={10} style={{ cursor: 'pointer', opacity: 0.6, color: deletingCommentId === reply.id ? '#f43f5e' : 'inherit' }} onClick={() => handleDeleteComment(post.id, reply.id)} />
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {editingComment?.id === reply.id ? (
                                      <div style={{ marginTop: '0.4rem' }}>
                                        <input
                                          autoFocus
                                          className="cyber-input"
                                          value={editingComment.content}
                                          onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleUpdateComment(post.id, reply.id, editingComment.content);
                                            if (e.key === 'Escape') setEditingComment(null);
                                          }}
                                          style={{ width: '100%', fontSize: '0.8rem', padding: '3px 6px' }}
                                        />
                                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end', marginTop: '0.3rem' }}>
                                          <button onClick={() => handleUpdateComment(post.id, reply.id, editingComment.content)} style={{ background: 'var(--accent-primary)', color: 'white', border: 'none', borderRadius: '4px', padding: '1px 6px', fontSize: '0.65rem', fontWeight: 800, cursor: 'pointer' }}>{t('save_upper')}</button>
                                          <button onClick={() => setEditingComment(null)} style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)', border: 'none', borderRadius: '4px', padding: '1px 6px', fontSize: '0.65rem', fontWeight: 800, cursor: 'pointer' }}>{t('cancel_upper')}</button>
                                        </div>
                                      </div>
                                    ) : (
                                      <p style={{ fontSize: '0.8rem', color: 'var(--text-body)', marginTop: '0.2rem', lineHeight: '1.4' }}>{reply.content}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  }
                </div>
              ))
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR: Stats & Name Days */}
        <div className="animate-fade hide-mobile">
          <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--accent-primary)', textTransform: 'uppercase', fontSize: '0.85rem', fontWeight: 900, letterSpacing: '0.1em', marginBottom: '1.5rem' }}>
              <Award size={18} /> {t('todays_occasions')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {celebrations.length > 0 ? celebrations.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(236, 72, 153, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Award size={16} color="var(--accent-magenta)" />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-heading)', fontWeight: 700 }}>{c.full_name || c.username}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.name_day} {t('name_day_label')}</div>
                  </div>
                </div>
              )) : <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('no_celebrations')}</p>}
            </div>
          </div>

          <div className="glass-card" style={{ padding: '1.5rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--accent-cyan)', textTransform: 'uppercase', fontSize: '0.85rem', fontWeight: 900, letterSpacing: '0.1em', marginBottom: '1.5rem' }}>
              <BarChart2 size={18} /> {t('network_insights')}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(34, 211, 238, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Users size={14} color="var(--accent-cyan)" />
                  </div>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>{t('total_members_label')}</span>
                </div>
                <span style={{ fontWeight: 800, color: 'var(--text-heading)' }}>{stats.members}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE POST MODAL */}
      {
        isCreatorOpen && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(15px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
            <div className="glass-card animate-scale-up" style={{ maxWidth: '650px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '28px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 60px rgba(0,0,0,0.5)' }}>

              {/* Modal Header */}
              <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-cyan))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)' }}>
                    <Zap size={20} color="white" />
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: 900,
                    color: 'var(--text-heading)',
                    letterSpacing: '-0.02em'
                  }}>{editingPostId ? t('modify_post_title') : 'Strategic Command Center'}</h3>
                </div>
                <div
                  onClick={handleCloseAttempt}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  className="hover-bright"
                >
                  <CloseIcon size={18} color="var(--text-muted)" />
                </div>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '2rem', flex: 1, overflowY: 'auto' }}>
                <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1.5rem' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '16px', border: '2px solid var(--accent-primary)', overflow: 'hidden', flexShrink: 0, boxShadow: '0 0 20px rgba(99, 102, 241, 0.2)' }}>
                    {profile?.avatar_url ? <img src={profile.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ background: 'var(--bg-deep)', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 900, color: 'var(--accent-primary)' }}>{currentUser?.username[0].toUpperCase()}</div>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: 'var(--text-heading)', fontSize: '1.1rem', fontWeight: 900, marginBottom: '2px' }}>{profile?.full_name || currentUser?.username}</h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }}></div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Strategic Intelligence Node</span>
                    </div>
                  </div>
                </div>

                <textarea
                  className="cyber-input"
                  style={{ width: '100%', minHeight: '140px', border: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)', fontSize: '1.1rem', resize: 'none', padding: '1.25rem', borderRadius: '16px', color: 'var(--text-heading)', marginBottom: '1.5rem' }}
                  placeholder={postMetadata.type === 'celebration' ? 'Describe the achievement...' : t('share_insights_placeholder')}
                  value={newPost}
                  onChange={e => setNewPost(e.target.value)}
                />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                  {/* Personnel Tagging Section */}
                  <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '20px', border: '1px solid var(--glass-border)' }}>
                     <label style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem', display: 'block' }}>
                      {postMetadata.type === 'celebration' ? 'Recipient of Recognition' : 'Collaborators & Stakeholders'}
                    </label>
                    <TagPersonnel
                      allUsers={allUsers}
                      selectedIds={postMetadata.mentions}
                                      onToggle={toggleMention}
                      currentUser={currentUser}
                    />
                  </div>

                  {/* Dynamic Post Sections */}
                  {/* Strategic Asset Section */}
                  <div className="input-group">
                    <label style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem', display: 'block' }}>
                      Strategic Visualization
                    </label>
                    <input type="file" ref={fileInputRef} onChange={handleMediaUpload} style={{ display: 'none' }} accept="image/*" />

                    {!postMetadata.media_url ? (
                      <div onClick={() => fileInputRef.current.click()} style={{ height: '160px', background: 'rgba(255,255,255,0.02)', borderRadius: '20px', border: '2px dashed var(--glass-border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem', cursor: 'pointer', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' }} className="hover-bright group">
                        {isUploadingMedia ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                            <div className="animate-spin" style={{ width: '24px', height: '24px', border: '3px solid var(--accent-primary)', borderTopColor: 'transparent', borderRadius: '50%' }}></div>
                            <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--accent-primary)', letterSpacing: '0.05em' }}>UPLOADING...</span>
                          </div>
                        ) : (
                          <>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-primary)' }}>
                              <ImageIcon size={24} />
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ color: 'var(--text-heading)', fontWeight: 800, fontSize: '0.9rem' }}>Deploy Visual Intelligence</div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '4px' }}>PNG, JPG or WebP (Max 5MB)</div>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div style={{ position: 'relative', borderRadius: '20px', overflow: 'hidden', border: '1px solid var(--glass-border)', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
                        <img src={postMetadata.media_url} style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', display: 'block' }} />
                        <div style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => fileInputRef.current.click()} style={{ padding: '8px 16px', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }} className="hover-bright">
                            <Sparkles size={14} /> Change
                          </button>
                          <button onClick={() => setPostMetadata(prev => ({ ...prev, media_url: '' }))} style={{ width: '32px', height: '32px', background: 'rgba(239, 68, 68, 0.8)', backdropFilter: 'blur(10px)', border: 'none', color: 'white', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} className="hover-bright">
                            <CloseIcon size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {postMetadata.type === 'poll' && (
                    <div className="poll-creator-container animate-fade" style={{
                      display: 'flex', flexDirection: 'column', gap: '1.5rem', padding: '1.5rem',
                      background: 'rgba(99, 102, 241, 0.03)', borderRadius: '24px', border: '1px solid rgba(99, 102, 241, 0.2)', position: 'relative'
                    }}>
                      <div className="input-group">
                        <label style={{ color: 'var(--accent-primary)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', display: 'block' }}>{t('poll_question')}</label>
                        <input
                          className="cyber-input"
                          style={{ width: '100%', border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(0,0,0,0.2)', fontSize: '1rem', fontWeight: 700 }}
                          placeholder="What is the strategic priority?"
                          value={postMetadata.poll_question}
                          onChange={e => setPostMetadata({ ...postMetadata, poll_question: e.target.value })}
                        />
                      </div>

                      <div className="input-group">
                        <label style={{ color: 'var(--accent-cyan)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '1rem', display: 'block' }}>Intelligence Options</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {(postMetadata.poll_options.length === 0 ? ['', ''] : postMetadata.poll_options).map((opt, i) => (
                            <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                              <input
                                className="cyber-input"
                                placeholder={`Option ${i + 1}`}
                                value={opt}
                                style={{ flex: 1, padding: '0.75rem 1rem', fontSize: '0.9rem', background: 'rgba(0,0,0,0.1)', border: '1px solid var(--glass-border)' }}
                                onChange={e => {
                                  const newOpts = [...(postMetadata.poll_options.length === 0 ? ['', ''] : postMetadata.poll_options)];
                                  newOpts[i] = e.target.value;
                                  setPostMetadata({ ...postMetadata, poll_options: newOpts });
                                }}
                              />
                              {postMetadata.poll_options.length > 2 && (
                                <button
                                  onClick={() => setPostMetadata({ ...postMetadata, poll_options: postMetadata.poll_options.filter((_, idx) => idx !== i) })}
                                  style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <CloseIcon size={16} />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            style={{ width: '100%', padding: '1rem', borderRadius: '14px', border: '1px dashed var(--glass-border)', background: 'transparent', color: 'var(--text-muted)', fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s ease' }}
                            onClick={() => setPostMetadata({ ...postMetadata, poll_options: [...(postMetadata.poll_options.length === 0 ? ['', ''] : postMetadata.poll_options), ''] })}
                            className="hover-bright"
                          >
                            + Add Strategic Option
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {postMetadata.type === 'event' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', padding: '1.5rem', background: 'rgba(129, 140, 248, 0.03)', borderRadius: '24px', border: '1px solid rgba(129, 140, 248, 0.2)' }}>
                      <div className="input-group">
                        <label style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', display: 'block' }}>Temporal Mapping</label>
                        <input type="datetime-local" className="cyber-input" value={postMetadata.event_date} style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)' }} onChange={e => setPostMetadata({ ...postMetadata, event_date: e.target.value })} />
                      </div>
                      <div className="input-group">
                        <label style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', display: 'block' }}>Operational Venue</label>
                        <input className="cyber-input" placeholder="Boardroom, Zoom, etc." style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--glass-border)' }} value={postMetadata.location} onChange={e => setPostMetadata({ ...postMetadata, location: e.target.value })} />
                      </div>
                    </div>
                  )}

                  {postMetadata.type === 'celebration' && (
                    <div style={{ position: 'relative' }}>
                      <label style={{ color: 'var(--accent-magenta)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem', display: 'block' }}>Recognition Category</label>
                      <div
                        className="cyber-input"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'rgba(236, 72, 153, 0.05)', border: '1px solid rgba(236, 72, 153, 0.2)', padding: '1rem 1.25rem' }}
                        onClick={() => setIsCelebrationDropdownOpen(!isCelebrationDropdownOpen)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Award size={18} color="var(--accent-magenta)" />
                          <span style={{ color: postMetadata.celebration_type ? 'var(--text-heading)' : 'var(--text-muted)', fontWeight: 800 }}>
                            {postMetadata.celebration_type || 'Select Category...'}
                          </span>
                        </div>
                        <ChevronDown size={18} style={{ transition: 'transform 0.3s ease', transform: isCelebrationDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--text-muted)' }} />
                      </div>

                      {isCelebrationDropdownOpen && (
                        <div className="glass-card animate-scale-up" style={{ position: 'absolute', top: 'calc(100% + 0.5rem)', left: 0, right: 0, zIndex: 100, padding: '0.75rem', background: '#1a1b26', boxShadow: '0 20px 40px rgba(0,0,0,0.6)', border: '1px solid var(--glass-border)', borderRadius: '16px' }}>
                          {CELEBRATION_TYPES.map((type) => (
                            <div
                              key={type.id}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.85rem 1rem', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s ease', background: postMetadata.celebration_type === type.id ? 'rgba(236, 72, 153, 0.1)' : 'transparent', color: postMetadata.celebration_type === type.id ? 'var(--accent-magenta)' : 'var(--text-body)' }}
                              onClick={() => { setPostMetadata({ ...postMetadata, celebration_type: type.id }); setIsCelebrationDropdownOpen(false); }}
                            >
                              {type.icon}
                              <span style={{ fontWeight: 700 }}>{type.id}</span>
                              {postMetadata.celebration_type === type.id && <Check size={14} style={{ marginLeft: 'auto' }} />}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{ padding: '1.5rem 2rem', background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--glass-border)' }}>
                <button
                  onClick={handleCloseAttempt}
                  style={{
                    padding: '0.85rem 1.75rem',
                    borderRadius: '14px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid var(--glass-border)',
                    color: 'var(--text-muted)',
                    fontSize: '0.85rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase'
                  }}
                  className="hover-bright"
                >Cancel</button>
                <button
                  className="btn-cyber"
                  style={{
                    padding: '0.85rem 2.25rem',
                    background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-cyan))',
                    border: 'none',
                    color: 'white',
                    fontSize: '0.85rem',
                    fontWeight: 900,
                    borderRadius: '14px',
                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)',
                    minWidth: '220px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase'
                  }}
                  onClick={handlePost}
                >
                  <Send size={18} />
                  <span>{editingPostId ? 'Refine Update' : 'PUBLISH'}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      <style>{`
        .post-tool-btn {
          background: transparent;
          border: none;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--text-muted);
          padding: 0.75rem 1rem;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-weight: 600;
        }
        .post-tool-btn:hover {
          background: rgba(255,255,255,0.05);
          color: var(--btn-text);
        }
        .post-action-btn {
          background: transparent;
          border: none;
          color: var(--text-muted);
          padding: 1rem;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 8px;
        }
        .post-action-btn:hover {
          color: var(--accent-primary);
        }
        .celebration-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: radial-gradient(circle at 2px 2px, rgba(236, 72, 153, 0.2) 1px, transparent 0);
          background-size: 24px 24px;
          opacity: 0.3;
          animation: floatSlow 20s linear infinite;
        }
        @keyframes floatSlow {
          from { background-position: 0 0; }
          to { background-position: 100px 100px; }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(236, 72, 153, 0.2); border-color: rgba(236, 72, 153, 0.3); }
          50% { box-shadow: 0 0 40px rgba(236, 72, 153, 0.4); border-color: rgba(236, 72, 153, 1); }
        }
        .hover-bright:hover {
          background: rgba(255,255,255,0.05) !important;
          color: var(--text-heading) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .clickable-profile-card:hover {
          border-color: var(--accent-primary) !important;
          box-shadow: 0 20px 40px -12px rgba(99, 102, 241, 0.2) !important;
        }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .poll-creator-container {
          animation: pollContainerPulse 4s infinite alternate ease-in-out;
        }

        @keyframes pollContainerPulse {
          0% { border-color: var(--glass-border); }
          100% { border-color: rgba(99, 102, 241, 0.3); }
        }

        .poll-delete-btn:hover {
          background: rgba(244, 114, 182, 0.2) !important;
          transform: rotate(90deg) scale(1.1);
        }

        .poll-add-btn:hover {
          background: rgba(99, 102, 241, 0.05) !important;
          border-color: var(--accent-primary) !important;
          color: var(--accent-primary) !important;
          transform: translateY(-2px);
        }

        .poll-option-row:focus-within {
          transform: translateX(4px);
        }

        .celebration-option:hover {
          background: rgba(99, 102, 241, 0.05) !important;
          color: var(--accent-primary) !important;
          transform: translateX(4px);
        }

        .poll-options-list::-webkit-scrollbar {
          width: 5px;
        }
        .poll-options-list::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.01);
          border-radius: 10px;
        }
        .poll-options-list::-webkit-scrollbar-thumb {
          background: var(--accent-primary);
          border-radius: 10px;
          border: 1px solid transparent;
        }
        .poll-options-list::-webkit-scrollbar-thumb:hover {
          background: var(--accent-cyan);
        }
      `}</style>
      <EngagementModal
        isOpen={engagementModal.isOpen}
        onClose={() => setEngagementModal({ ...engagementModal, isOpen: false })}
        title={engagementModal.title}
        postId={engagementModal.postId}
        type={engagementModal.type}
        pollOptions={engagementModal.pollOptions}
      />
      <ShareModal
        {...shareModal}
        onClose={() => setShareModal({ ...shareModal, isOpen: false })}
      />
      <ConfirmDialog
        isOpen={showConfirm}
        title={t('unsaved_changes_title')}
        message={t('unsaved_changes_message')}
        onSave={() => {
          handlePost();
          setShowConfirm(false);
        }}
        onConfirm={closeCreator}
        onCancel={() => setShowConfirm(false)}
      />
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={confirmDelete}
        onCancel={() => { setShowDeleteConfirm(false); setPostIdToDelete(null); }}
        title={t('delete_post_title')}
        message={t('delete_post_message')}
      />
    </>
  );
}

export default Home;
